import React, { useState, useRef, useEffect } from 'react';
import { MapPin, ChevronDown, Check } from 'lucide-react';
import { useCity } from '../contexts/CityContext';

/**
 * City selector chip for the Header. Shows the active city; for users with
 * more than one city (or super-admins) it opens a dropdown to switch. A
 * single-city user just sees a static label. Switching reloads the app
 * (CityContext) so every tab refetches under the new city.
 */
export default function CitySelector() {
  const { currentCity, setCurrentCity, cities, loading } = useCity();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const current = cities.find((c) => c.projectId === currentCity);
  const label = current ? current.displayName : (loading ? 'Loading…' : (currentCity || 'No city'));
  const canSwitch = cities.length > 1;

  const chipInner = (
    <>
      <MapPin size={16} color="#c8a147" />
      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary, #0f172a)' }}>{label}</span>
      {canSwitch && <ChevronDown size={16} color="var(--text-secondary, #64748b)" />}
    </>
  );

  const chipStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 12px',
    borderRadius: 10,
    border: '1px solid var(--border-color, #e2e8f0)',
    background: '#fff',
  };

  if (!canSwitch) {
    return <div style={chipStyle} title="Current city">{chipInner}</div>;
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{ ...chipStyle, cursor: 'pointer' }}
      >
        {chipInner}
      </button>
      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            minWidth: 220,
            background: '#fff',
            border: '1px solid var(--border-color, #e2e8f0)',
            borderRadius: 10,
            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.12)',
            zIndex: 50,
            padding: 6,
          }}
        >
          {cities.map((c) => {
            const active = c.projectId === currentCity;
            return (
              <button
                key={c.projectId}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => { setOpen(false); setCurrentCity(c.projectId); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '8px 12px',
                  border: 'none',
                  background: active ? 'rgba(200, 161, 71, 0.12)' : 'transparent',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: active ? 600 : 500,
                  color: 'var(--text-primary, #0f172a)',
                  textAlign: 'left',
                }}
              >
                <span>{c.displayName}</span>
                {active && <Check size={16} color="#c8a147" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
